// bluaIQ — Diagnostic Takeoff v3 — config / feature flags
// Centralised flag map for the diagnostic-only takeoff v3 surface.
//
// SAFETY POSTURE (do not relax without explicit instruction):
//   * legacy takeoff stays enabled and untouched
//   * diagnostic v3 is candidate-only; nothing it produces flows
//     into legacy takeoff or takeoff-v2 facts
//   * final-quantity / Gate3 / BOQ / purchase-quantity / accepted-
//     evidence flags stay false in this milestone
//
// All consumers must read flags via getDiagnosticV3Config() —
// never pluck process.env directly — so the safety posture stays
// centralised and auditable.
//
// GENERALISATION RULE: this file holds NO project- or fixture-
// specific values. No addresses, sheet numbers, page indexes,
// crop coords, bbox coords, marks, schedules, or titleblock data.

export type TakeoffEngine = 'legacy' | 'diagnostic-v3'

export interface DiagnosticV3Config {
  /** Which takeoff engine is the active source of truth in the UI.
   *  This milestone keeps "legacy" as the active engine; v3 lives
   *  alongside as a diagnostic surface only. */
  TAKEOFF_ENGINE: TakeoffEngine
  /** Legacy takeoff (M1 TakeoffModule + takeoff-v2 shadow) stays on
   *  for *reference / shadow / internal debugging only*. Customer-
   *  facing finality flags below are independent. */
  TAKEOFF_LEGACY_ENABLED: boolean
  /** Diagnostic v3 surface visible to the UI. Defaults true. */
  TAKEOFF_DIAGNOSTIC_V3_ENABLED: boolean

  // ── Legacy containment flags (always false in this milestone) ──
  // Block legacy takeoff from being treated as a customer-facing
  // final-quantity engine. These flags exist because legacy takeoff
  // has produced duplicated quantities in the past; until source-
  // backed evidence gates mature, the legacy engine cannot be
  // promoted to "official" customer output.
  /** Customer-visible legacy outputs framed as final/official. */
  TAKEOFF_LEGACY_CUSTOMER_OUTPUTS_ENABLED: false
  /** "Confirm & Approve" / quantity finalisation on legacy takeoffs. */
  TAKEOFF_LEGACY_QUANTITY_APPROVAL_ENABLED: false
  /** Purchase / order generation from legacy takeoff outputs. */
  TAKEOFF_LEGACY_PURCHASE_OUTPUTS_ENABLED: false
  /** BOM / BOQ export from legacy takeoff outputs. */
  TAKEOFF_LEGACY_BOM_EXPORT_ENABLED: false
  /** Use Diagnostic v3 as the *replacement path* for new customer
   *  takeoff work. Defaults true. UI/router still keeps the route
   *  developer-only. Service code reads this to decide where new
   *  takeoff workflows should land. */
  TAKEOFF_DIAGNOSTIC_V3_REPLACEMENT_ENABLED: boolean

  /** Final accepted quantities. Always false in this milestone. */
  TAKEOFF_FINAL_QUANTITIES_ENABLED: false
  /** Gate 3 final calculation. Always false in this milestone. */
  TAKEOFF_GATE3_ENABLED: false
  /** Bill of quantities export. Always false in this milestone. */
  TAKEOFF_BOQ_ENABLED: false
  /** Purchase quantities. Always false in this milestone. */
  TAKEOFF_PURCHASE_QUANTITIES_ENABLED: false
}

const TRUTHY = new Set(['1', 'true', 'yes', 'on'])

function getEnv(name: string): string {
  return (typeof process !== 'undefined' && process.env ? process.env[name] : undefined) || ''
}

function readBoolEnv(name: string, fallback: boolean): boolean {
  const raw = getEnv(name)
  if (!raw) return fallback
  return TRUTHY.has(raw.toLowerCase().trim())
}

/**
 * Gate-flag resolver. The only way to flip these from false is to
 * change this file (and ship code that respects the new value).
 * Env overrides are observed only for audit purposes.
 */
function readGateFlag(name: string): false {
  void readBoolEnv(name, false)
  return false
}

function readEngineEnv(): TakeoffEngine {
  const raw = getEnv('TAKEOFF_ENGINE').toLowerCase().trim()
  if (raw === 'diagnostic-v3') return 'diagnostic-v3'
  return 'legacy'
}

export function getDiagnosticV3Config(): DiagnosticV3Config {
  return {
    TAKEOFF_ENGINE: readEngineEnv(),
    TAKEOFF_LEGACY_ENABLED: readBoolEnv('TAKEOFF_LEGACY_ENABLED', true),
    TAKEOFF_DIAGNOSTIC_V3_ENABLED: readBoolEnv('TAKEOFF_DIAGNOSTIC_V3_ENABLED', true),
    TAKEOFF_LEGACY_CUSTOMER_OUTPUTS_ENABLED: readGateFlag('TAKEOFF_LEGACY_CUSTOMER_OUTPUTS_ENABLED'),
    TAKEOFF_LEGACY_QUANTITY_APPROVAL_ENABLED: readGateFlag('TAKEOFF_LEGACY_QUANTITY_APPROVAL_ENABLED'),
    TAKEOFF_LEGACY_PURCHASE_OUTPUTS_ENABLED: readGateFlag('TAKEOFF_LEGACY_PURCHASE_OUTPUTS_ENABLED'),
    TAKEOFF_LEGACY_BOM_EXPORT_ENABLED: readGateFlag('TAKEOFF_LEGACY_BOM_EXPORT_ENABLED'),
    TAKEOFF_DIAGNOSTIC_V3_REPLACEMENT_ENABLED: readBoolEnv('TAKEOFF_DIAGNOSTIC_V3_REPLACEMENT_ENABLED', true),
    TAKEOFF_FINAL_QUANTITIES_ENABLED: readGateFlag('TAKEOFF_FINAL_QUANTITIES_ENABLED'),
    TAKEOFF_GATE3_ENABLED: readGateFlag('TAKEOFF_GATE3_ENABLED'),
    TAKEOFF_BOQ_ENABLED: readGateFlag('TAKEOFF_BOQ_ENABLED'),
    TAKEOFF_PURCHASE_QUANTITIES_ENABLED: readGateFlag('TAKEOFF_PURCHASE_QUANTITIES_ENABLED'),
  }
}

/**
 * Compact safety flag map. This is exactly the shape that gets
 * embedded in API responses, artifact payloads and the handoff JSON,
 * so ChatGPT review can verify the gate posture from any of those
 * surfaces without re-reading source.
 */
export interface DiagnosticTakeoffSafetyFlags {
  diagnostic_only: true
  diagnostic_v3_enabled: boolean
  legacy_enabled: boolean
  takeoff_engine: TakeoffEngine
  // Legacy containment posture (always false until source-backed
  // evidence gates mature):
  legacy_customer_outputs_allowed: false
  legacy_quantity_approval_allowed: false
  legacy_purchase_outputs_allowed: false
  legacy_bom_export_allowed: false
  // Diagnostic v3 is the replacement path for customer takeoff work:
  diagnostic_v3_replacement_enabled: boolean
  // Standing diagnostic v3 gates:
  final_quantities_allowed: false
  gate_3_allowed: false
  step_b_allowed: false
  real_step_b_allowed: false
  boq_allowed: false
  purchase_quantity_allowed: false
  quantity_allowed: false
  accepted_measurement_evidence_allowed: false
  accepted_final_quantity_allowed: false
  accepted_parent_boundary_allowed: false
  accepted_local_segment_width_allowed: false
  accepted_opening_quantities_allowed: false
  final_scale_lock_record_allowed: false
  takeoff_preview_allowed: false
}

export function getDiagnosticTakeoffSafetyFlags(): DiagnosticTakeoffSafetyFlags {
  const c = getDiagnosticV3Config()
  return {
    diagnostic_only: true,
    diagnostic_v3_enabled: c.TAKEOFF_DIAGNOSTIC_V3_ENABLED,
    legacy_enabled: c.TAKEOFF_LEGACY_ENABLED,
    takeoff_engine: c.TAKEOFF_ENGINE,
    legacy_customer_outputs_allowed: false,
    legacy_quantity_approval_allowed: false,
    legacy_purchase_outputs_allowed: false,
    legacy_bom_export_allowed: false,
    diagnostic_v3_replacement_enabled: c.TAKEOFF_DIAGNOSTIC_V3_REPLACEMENT_ENABLED,
    final_quantities_allowed: false,
    gate_3_allowed: false,
    step_b_allowed: false,
    real_step_b_allowed: false,
    boq_allowed: false,
    purchase_quantity_allowed: false,
    quantity_allowed: false,
    accepted_measurement_evidence_allowed: false,
    accepted_final_quantity_allowed: false,
    accepted_parent_boundary_allowed: false,
    accepted_local_segment_width_allowed: false,
    accepted_opening_quantities_allowed: false,
    final_scale_lock_record_allowed: false,
    takeoff_preview_allowed: false,
  }
}

/**
 * Compact map suitable for `safety_flags` in handoff JSON.
 */
export function getDiagnosticV3SafetyFlagMap(): Record<string, boolean | string> {
  return getDiagnosticTakeoffSafetyFlags() as unknown as Record<string, boolean | string>
}

/**
 * Throwing guard for any code path that tries to do something the
 * milestone forbids. Throwing keeps regressions loud — never silent.
 */
export function assertDiagnosticOnly(action: string): never {
  throw new Error(
    `diagnostic-takeoff-v3: action "${action}" is not allowed in the foundation milestone ` +
      `(diagnostic_only=true; no quantity / no final / no Step B / no Gate 3 / no BOQ).`,
  )
}
